import * as React from "react";
import {
  addFilesToForm,
  apiClient,
  formatLocalISODate,
  isApiError,
  type ApiResponse,
  type Files,
} from "@/api/_client";
import type { Project, ProjectDocument } from "@/api/projects";
import type { Portfolio, PortfolioDocument } from "@/api/portfolios";

// ============================================================================
// Types
// ============================================================================

export type FileStatus = "pending" | "uploading" | "completed" | "error";

export interface FileUploadState {
  status: "idle" | "uploading" | "completed" | "error" | "cancelled";
  totalFiles: number;
  completedFiles: number;
  currentBatch: number;
  totalBatches: number;
  fileStatuses: Map<string, FileStatus>;
  error?: string;
}

export interface BatchResult {
  batchIndex: number;
  success: boolean;
  fileCount: number;
  autoCategorizedDocs: (ProjectDocument | PortfolioDocument)[];
  ignoredDocs: string[];
  error?: string;
}

export interface AggregatedResult {
  success: boolean;
  totalUploaded: number;
  totalIgnored: number;
  autoCategorizedDocs: (ProjectDocument | PortfolioDocument)[];
  ignoredDocs: string[];
  batchResults: BatchResult[];
  errors: string[];
}

export interface UploadOptions {
  mode: "project" | "portfolio";
  instanceId: string;
  docCategory?: string;
  preserveFolderStructure?: boolean;
  chromefolderPaths?: Map<string, string>;
}

export interface UseFileUploadOptions {
  batchSize?: number;
  onBatchComplete?: (result: BatchResult) => void;
  onComplete?: (result: AggregatedResult) => void;
  onError?: (error: string, batchIndex: number) => void;
}

export interface UseFileUploadReturn {
  state: FileUploadState;
  upload: (files: File[], options: UploadOptions) => Promise<AggregatedResult>;
  cancel: () => void;
  reset: () => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Creates a unique key for a file to track its status.
 * Note: We only use name and size, not lastModified, because Chrome's
 * webkitGetAsEntry().file() API can return different lastModified values
 * than dataTransfer.files, causing key mismatches for folder path lookups.
 */
export function getFileKey(file: File): string {
  return `${file.name}|${file.size}`;
}

/**
 * Splits files into batches with corresponding metadata arrays
 */
function createBatches(
  files: File[],
  batchSize: number,
  chromefolderPaths?: Map<string, string>,
): {
  files: File[];
  modDates: string[];
  folderPaths: string[];
}[] {
  const batches: {
    files: File[];
    modDates: string[];
    folderPaths: string[];
  }[] = [];

  for (let i = 0; i < files.length; i += batchSize) {
    const batchFiles = files.slice(i, i + batchSize);
    const modDates: string[] = [];
    const folderPaths: string[] = [];

    batchFiles.forEach((file: File & { webkitRelativePath?: string }) => {
      // Collect modification dates
      if (file.lastModified) {
        modDates.push(formatLocalISODate(file.lastModified));
      } else {
        modDates.push("");
      }

      // Collect folder paths
      const firefoxPath = file.webkitRelativePath;
      const fileKey = getFileKey(file);
      const chromePath = chromefolderPaths?.get(fileKey);
      const folderPath = firefoxPath || chromePath || file.name;
      folderPaths.push(folderPath);
    });

    batches.push({ files: batchFiles, modDates, folderPaths });
  }

  return batches;
}

/**
 * Uploads a single batch of files
 */
async function uploadBatch(
  batch: { files: File[]; modDates: string[]; folderPaths: string[] },
  options: UploadOptions,
  signal: AbortSignal,
): Promise<
  ApiResponse<
    (Project | Portfolio) & {
      documents: Files;
      auto_categorized_docs?: (ProjectDocument | PortfolioDocument)[];
      ignored_docs?: string[];
    }
  >
> {
  const form = new FormData();
  addFilesToForm(form, batch.files);
  form.append("mod_dates", batch.modDates.join(","));

  let url: string;

  if (options.mode === "project") {
    url = `api/projects/${options.instanceId}/`;
  } else {
    url = `api/portfolios/${options.instanceId}/`;
  }

  // Add query parameters
  const params = new URLSearchParams();
  if (options.preserveFolderStructure) {
    params.append("preserve_folder_structure", "true");
    // Use ||| as delimiter since filenames can contain commas
    form.append("folder_paths", batch.folderPaths.join("|||"));
  }
  if (options.docCategory) {
    params.append("doc_category", options.docCategory);
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await apiClient.request<
    never,
    ApiResponse<
      (Project | Portfolio) & {
        documents: Files;
        auto_categorized_docs?: (ProjectDocument | PortfolioDocument)[];
        ignored_docs?: string[];
      }
    >
  >({
    method: "patch",
    url,
    headers: {
      "Content-Type": "multipart/form-data",
    },
    data: form,
    signal,
  });

  return response;
}

// ============================================================================
// Hook
// ============================================================================

const DEFAULT_BATCH_SIZE = 5;

const initialState: FileUploadState = {
  status: "idle",
  totalFiles: 0,
  completedFiles: 0,
  currentBatch: 0,
  totalBatches: 0,
  fileStatuses: new Map(),
};

export function useFileUpload(
  options: UseFileUploadOptions = {},
): UseFileUploadReturn {
  const {
    batchSize = DEFAULT_BATCH_SIZE,
    onBatchComplete,
    onComplete,
    onError,
  } = options;

  const [state, setState] = React.useState<FileUploadState>(initialState);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const isCancelledRef = React.useRef(false);

  // Warn user if they try to close/navigate away during upload
  React.useEffect(() => {
    if (state.status !== "uploading") return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore custom messages, but we need to set returnValue
      e.returnValue = "Upload in progress. Are you sure you want to leave?";
      return e.returnValue;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [state.status]);

  const reset = React.useCallback(() => {
    setState(initialState);
    isCancelledRef.current = false;
  }, []);

  const cancel = React.useCallback(() => {
    isCancelledRef.current = true;
    abortControllerRef.current?.abort();
    setState((prev) => ({
      ...prev,
      status: "cancelled",
    }));
  }, []);

  const upload = React.useCallback(
    async (
      files: File[],
      uploadOptions: UploadOptions,
    ): Promise<AggregatedResult> => {
      // Reset state
      isCancelledRef.current = false;
      abortControllerRef.current = new AbortController();

      // Initialize file statuses
      const fileStatuses = new Map<string, FileStatus>();
      files.forEach((file) => {
        fileStatuses.set(getFileKey(file), "pending");
      });

      // Create batches
      const batches = createBatches(
        files,
        batchSize,
        uploadOptions.chromefolderPaths,
      );

      setState({
        status: "uploading",
        totalFiles: files.length,
        completedFiles: 0,
        currentBatch: 0,
        totalBatches: batches.length,
        fileStatuses,
      });

      const batchResults: BatchResult[] = [];
      let totalUploaded = 0;
      const allAutoCategorizedDocs: (ProjectDocument | PortfolioDocument)[] =
        [];
      const allIgnoredDocs: string[] = [];
      const errors: string[] = [];

      // Process batches sequentially
      for (let i = 0; i < batches.length; i++) {
        if (isCancelledRef.current) {
          break;
        }

        const batch = batches[i];

        // Update current batch and mark files as uploading
        setState((prev) => {
          const newFileStatuses = new Map(prev.fileStatuses);
          batch.files.forEach((file) => {
            newFileStatuses.set(getFileKey(file), "uploading");
          });
          return {
            ...prev,
            currentBatch: i + 1,
            fileStatuses: newFileStatuses,
          };
        });

        try {
          const response = await uploadBatch(
            batch,
            uploadOptions,
            abortControllerRef.current.signal,
          );

          if (isApiError(response)) {
            // Batch failed
            const errorMsg =
              response.formErrors?.documents ||
              response.error ||
              "Upload failed";

            setState((prev) => {
              const newFileStatuses = new Map(prev.fileStatuses);
              batch.files.forEach((file) => {
                newFileStatuses.set(getFileKey(file), "error");
              });
              return {
                ...prev,
                status: "error",
                error: errorMsg,
                fileStatuses: newFileStatuses,
              };
            });

            batchResults.push({
              batchIndex: i,
              success: false,
              fileCount: batch.files.length,
              autoCategorizedDocs: [],
              ignoredDocs: [],
              error: errorMsg,
            });

            errors.push(errorMsg);
            onError?.(errorMsg, i);

            // Stop on error (as per user preference)
            break;
          } else {
            // Batch succeeded
            const autoDocs = response.data.auto_categorized_docs || [];
            const ignoredDocs = response.data.ignored_docs || [];
            const uploadedCount = batch.files.length - ignoredDocs.length;

            totalUploaded += uploadedCount;
            allAutoCategorizedDocs.push(...autoDocs);
            allIgnoredDocs.push(...ignoredDocs);

            setState((prev) => {
              const newFileStatuses = new Map(prev.fileStatuses);
              batch.files.forEach((file) => {
                newFileStatuses.set(getFileKey(file), "completed");
              });
              return {
                ...prev,
                completedFiles: prev.completedFiles + batch.files.length,
                fileStatuses: newFileStatuses,
              };
            });

            const batchResult: BatchResult = {
              batchIndex: i,
              success: true,
              fileCount: batch.files.length,
              autoCategorizedDocs: autoDocs,
              ignoredDocs,
            };

            batchResults.push(batchResult);
            onBatchComplete?.(batchResult);
          }
        } catch (err) {
          // Handle abort/cancel
          if (err instanceof Error && err.name === "AbortError") {
            break;
          }

          // Handle unexpected errors
          const errorMsg = err instanceof Error ? err.message : "Upload failed";

          setState((prev) => {
            const newFileStatuses = new Map(prev.fileStatuses);
            batch.files.forEach((file) => {
              newFileStatuses.set(getFileKey(file), "error");
            });
            return {
              ...prev,
              status: "error",
              error: errorMsg,
              fileStatuses: newFileStatuses,
            };
          });

          batchResults.push({
            batchIndex: i,
            success: false,
            fileCount: batch.files.length,
            autoCategorizedDocs: [],
            ignoredDocs: [],
            error: errorMsg,
          });

          errors.push(errorMsg);
          onError?.(errorMsg, i);
          break;
        }
      }

      // Determine final status
      const wasCancelled = isCancelledRef.current;
      const hasErrors = errors.length > 0;
      const finalStatus = wasCancelled
        ? "cancelled"
        : hasErrors
          ? "error"
          : "completed";

      setState((prev) => ({
        ...prev,
        status: finalStatus,
      }));

      const result: AggregatedResult = {
        success: !hasErrors && !wasCancelled,
        totalUploaded,
        totalIgnored: allIgnoredDocs.length,
        autoCategorizedDocs: allAutoCategorizedDocs,
        ignoredDocs: allIgnoredDocs,
        batchResults,
        errors,
      };

      onComplete?.(result);

      return result;
    },
    [batchSize, onBatchComplete, onComplete, onError],
  );

  return {
    state,
    upload,
    cancel,
    reset,
  };
}
